/**
 * Reports.js — the report centre
 *
 * Brian, 15 September 2026: *"for reports, lets start beefing this up with
 * real reports across the board ... The current reports are very lackluster
 * and need to be massive improved."*
 *
 * Three things this page does that the old one did not.
 *
 * **It is built from the catalogue.** `GET /reports/catalog/` returns every
 * report with its family, its parameters, its formats and — where it has one
 * — the caveat a reader needs before quoting the figure. A report added on
 * the backend appears here with no frontend build, which matters because
 * shipping this page means committing a rebuilt bundle (CLAUDE.md §2).
 *
 * **It shows the figures on screen, not only as a download.** A report that
 * can only be downloaded is a report nobody checks: you have to want the
 * answer enough to open a PDF. The four statements render here as tables,
 * with the balance check, the FX banner and the completeness notes on the
 * face of them, and the download buttons are beside the numbers rather than
 * instead of them.
 *
 * **It sends `format`.** The old page's own comment read "format is used for
 * UI only, not sent to backend", so its Excel/PDF toggle selected nothing —
 * and had it sent the parameter it would have got a 404, because DRF
 * reserves `format` and every report view declared a renderer whose format
 * was `None`. Both ends are fixed; this is the end that asks.
 *
 * One rule runs through the formatting, and it is the same one the backend
 * and the PDFs follow (CLAUDE.md §0.465): **a figure the book could not
 * produce reads `n/a`, never `0.00`.** `usd: null` off any of these
 * endpoints means a currency had no exchange rate loaded. Printed as zero it
 * claims the line is worth nothing; printed with a dollar sign on a krona
 * number it is the mislabelled fallback that put SEK 46.7m into a dollar
 * total. Native figures always carry their currency code.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/client";
import RepatriationView from "../components/RepatriationReport";
import {
  C, nf, num, usd, when, Banner, Grid, Kpi, KpiRow, td, tdR,
} from "../components/reportUi";


const FAMILY_ICON = {
  "Financial statements": "\u{1F4D2}",
  Performance: "\u{1F4C8}",
  "Investor reporting": "\u{1F4E8}",
  Positions: "\u{1F4CA}",
  Operations: "\u{1F3E6}",
};

/* The reports this page can render on screen, and how each one lays out.
   Anything else is download-only, which is honest: a table this page cannot
   draw is better linked than half-drawn. */
const VIEWABLE = {
  balance_sheet: "statement",
  income_statement: "statement",
  cash_flow: "cashflow",
  trial_balance: "trialbalance",
  portfolio_exposure: "exposure",
  risk: "risk",
  realized_pnl: "realized",
  unrealized_pnl: "unrealized",
  cash_repatriation: "repatriation",
};

/** `SEK 46,711,954.53 · USD 920,000.00` — every currency, each named. */
function native(byCurrency) {
  if (!byCurrency) return "—";
  const keys = Object.keys(byCurrency).filter((k) => Number(byCurrency[k]) !== 0);
  if (!keys.length) return "—";
  return keys
    .sort()
    .map((k) => `${k} ${nf(2).format(Number(byCurrency[k]))}`)
    .join(" · ");
}

/* The five account families, spelled as the chart of accounts spells them.
   `unclassified` is its own heading rather than being folded anywhere,
   because an account in no family is in no total and wants saying. */
const TYPE_LABEL = {
  asset: "Assets",
  liability: "Liabilities",
  equity: "Equity",
  income: "Income",
  expense: "Expenses",
  unclassified: "Unclassified — in no total",
};

function typeLabel(kind) {
  if (!kind) return TYPE_LABEL.unclassified;
  return TYPE_LABEL[kind] || String(kind).replace(/^./, (c) => c.toUpperCase());
}

/* ─── Small pieces ───────────────────────────────────────────── */

const label = {
  display: "block", fontSize: 10, fontWeight: 600, color: C.muted,
  marginBottom: 4, textTransform: "uppercase", letterSpacing: ".5px",
};

const input = {
  padding: "7px 10px", borderRadius: 6, border: `1px solid ${C.border}`,
  background: C.inputBg, color: C.text, fontSize: 13, boxSizing: "border-box",
  width: "100%", minWidth: 130,
};

/* A statement row: section, group, line or total. The hierarchy is the whole
   point of a statement, so it is in the markup rather than in indentation
   alone. */
function Row({ kind, cells }) {
  const base =
    kind === "section"
      ? { background: C.sand, fontWeight: 700 }
      : kind === "group"
      ? { background: C.cream, fontWeight: 600 }
      : kind === "total"
      ? { background: C.cream, fontWeight: 700, borderTop: `2px solid ${C.navy}` }
      : {};
  return (
    <tr style={base}>
      {cells.map((cell, i) => (
        <td
          key={i}
          style={{
            ...(cell.right ? tdR : td),
            ...(i === 0 && kind === "line" ? { paddingLeft: 24 } : null),
            ...(cell.muted ? { color: C.muted, fontSize: 11 } : null),
          }}
          title={cell.title}
        >
          {cell.value}
        </td>
      ))}
    </tr>
  );
}

/* ─── The FX / completeness banner every statement gets ──────── */

function FxBanner({ report }) {
  const fx = report?.fx;
  if (!fx || fx.currency_safe) return null;
  return (
    <Banner tone="bad" title="The USD column is incomplete.">
      No exchange rate is loaded for {fx.unconverted_currencies.join(", ")} on
      or before {when(report.as_of)}, so every dollar figure touching one of
      those currencies reads <strong>n/a</strong> rather than a face-value
      total. The native-currency figures are complete. Run{" "}
      <code>pipe.py fetch-fx --commit</code> to load them.
    </Banner>
  );
}

/* ─── Balance sheet / income statement ───────────────────────── */

function StatementView({ report }) {
  const isBalanceSheet = report.report === "balance_sheet";
  const sides = isBalanceSheet
    ? ["assets", "liabilities", "equity", "unclassified"]
    : ["income", "expenses"];

  const rows = [];
  sides.forEach((side) => {
    const section = report[side];
    if (!section || !section.groups?.length) return;
    rows.push({
      kind: "section",
      cells: [
        { value: section.label },
        { value: usd(section.total.mtd.usd), right: true },
        { value: usd(section.total.ytd.usd), right: true },
        { value: usd(section.total.itd.usd), right: true },
        { value: native(section.total.itd.by_currency), right: true, muted: true },
      ],
    });
    section.groups.forEach((group) => {
      rows.push({
        kind: "group",
        cells: [
          { value: group.group },
          { value: usd(group.total.mtd.usd), right: true },
          { value: usd(group.total.ytd.usd), right: true },
          { value: usd(group.total.itd.usd), right: true },
          { value: native(group.total.itd.by_currency), right: true, muted: true },
        ],
      });
      group.lines.forEach((line) => {
        rows.push({
          kind: "line",
          cells: [
            {
              value: (
                <>
                  {line.label}
                  {line.intercompany && (
                    <span style={{ color: C.gold, fontSize: 10 }}> · intercompany</span>
                  )}
                  <div style={{ fontSize: 10, color: C.muted }}>{line.account}</div>
                </>
              ),
              title: line.note || line.account,
            },
            { value: usd(line.mtd.usd), right: true },
            { value: usd(line.ytd.usd), right: true },
            { value: usd(line.itd.usd), right: true },
            { value: native(line.itd.by_currency), right: true, muted: true },
          ],
        });
      });
    });
    if (isBalanceSheet && side === "assets") {
      rows.push(totalRow("TOTAL ASSETS", report.totals.assets));
    }
  });

  if (isBalanceSheet) {
    rows.push(totalRow("TOTAL LIABILITIES AND EQUITY", report.totals.liabilities_and_equity));
  } else {
    rows.push(totalRow("NET INCOME", report.net_income));
  }

  const check = report.balance_check;
  return (
    <>
      <FxBanner report={report} />
      {isBalanceSheet && check && !check.balanced && (
        <Banner tone="bad" title="This balance sheet does not balance.">
          Debits less credits over every account comes to{" "}
          <strong>{usd(check.usd_residual)}</strong> instead of nil, which means
          something posted one-sided. The per-currency residuals are{" "}
          {check.per_currency
            .map((r) => `${r.currency} ${nf(2).format(Number(r.residual))}`)
            .join(", ")}
          .
        </Banner>
      )}

      <KpiRow>
        {isBalanceSheet ? (
          <>
            <Kpi name="Total assets" value={usd(report.totals.assets.balance.usd, { cents: false })} />
            <Kpi
              name="Liabilities and equity"
              value={usd(report.totals.liabilities_and_equity.balance.usd, { cents: false })}
            />
            <Kpi
              name="Net income to date"
              value={usd(report.net_income_to_date.itd.usd, { cents: false })}
              hint="Income less expenses. The book has never been closed, so it is not yet in any Retained Earnings account."
            />
            <Kpi
              name="Balance check"
              value={check?.balanced ? "Balanced" : usd(check?.usd_residual)}
              tone={check?.balanced ? "good" : "bad"}
            />
            <Kpi
              name="Intercompany, group-wide"
              value={usd(report.intercompany.usd)}
              hint={`${report.intercompany.accounts} accounts. One entity holding another's money is one loan seen twice, so group-wide it should cancel.`}
              tone={Number(report.intercompany.usd) === 0 ? "good" : "bad"}
            />
          </>
        ) : (
          <>
            <Kpi name="Net income MTD" value={usd(report.net_income.mtd.usd, { cents: false })} />
            <Kpi name="Net income YTD" value={usd(report.net_income.ytd.usd, { cents: false })} />
            <Kpi name="Net income ITD" value={usd(report.net_income.itd.usd, { cents: false })} />
            <Kpi name="Income ITD" value={usd(report.income.total.itd.usd, { cents: false })} />
            <Kpi name="Expenses ITD" value={usd(report.expenses.total.itd.usd, { cents: false })} />
          </>
        )}
      </KpiRow>

      <Grid
        headers={[
          "Account",
          { name: "MTD", right: true },
          { name: "YTD", right: true },
          { name: isBalanceSheet ? "ITD / balance" : "ITD", right: true },
          { name: "Native currency", right: true },
        ]}
      >
        {rows.map((row, i) => (
          <Row key={i} kind={row.kind} cells={row.cells} />
        ))}
      </Grid>

      <Notes report={report} />
    </>
  );

  function totalRow(name, block) {
    return {
      kind: "total",
      cells: [
        { value: name },
        { value: usd(block.mtd.usd), right: true },
        { value: usd(block.ytd.usd), right: true },
        { value: usd(block.itd.usd), right: true },
        { value: native(block.itd.by_currency), right: true, muted: true },
      ],
    };
  }
}

/* ─── Cash flows ─────────────────────────────────────────────── */

function CashFlowView({ report }) {
  const itd = report.reconciliation.itd;
  const net = report.net_change_in_cash;
  const internal = report.internal_transfers.itd;
  const mixed = report.mixed_transactions.itd;

  const rows = [];
  report.sections.forEach((section) => {
    const hasAny =
      section.lines.length ||
      Object.keys(section.total.itd.by_currency || {}).length;
    if (!hasAny) return;
    rows.push({
      kind: "section",
      cells: [
        { value: section.label },
        { value: usd(section.total.mtd.usd), right: true },
        { value: usd(section.total.ytd.usd), right: true },
        { value: usd(section.total.itd.usd), right: true },
        { value: native(section.total.itd.by_currency), right: true, muted: true },
      ],
    });
    section.lines.forEach((line) => {
      rows.push({
        kind: "line",
        cells: [
          { value: line.account },
          { value: usd(line.mtd.usd), right: true },
          { value: usd(line.ytd.usd), right: true },
          { value: usd(line.itd.usd), right: true },
          { value: native(line.itd.by_currency), right: true, muted: true },
        ],
      });
    });
  });
  rows.push({
    kind: "total",
    cells: [
      { value: "NET CHANGE IN CASH" },
      { value: usd(net.mtd.usd), right: true },
      { value: usd(net.ytd.usd), right: true },
      { value: usd(net.itd.usd), right: true },
      { value: native(net.itd.by_currency), right: true, muted: true },
    ],
  });

  return (
    <>
      <FxBanner report={report} />
      {!itd.ties && (
        <Banner tone="bad" title="This statement does not reconcile to cash.">
          Opening cash plus the net change comes to{" "}
          <strong>{usd(itd.residual.usd)}</strong> away from what the cash
          accounts themselves hold, which means a cash leg was classified into
          no section. Look at <strong>Unclassified</strong> first.
        </Banner>
      )}

      <KpiRow>
        <Kpi name="Opening cash" value={usd(itd.opening_cash.usd, { cents: false })} />
        <Kpi name="Net change ITD" value={usd(net.itd.usd, { cents: false })} />
        <Kpi name="Closing cash" value={usd(itd.closing_cash.usd, { cents: false })} />
        <Kpi
          name="Reconciles to cash"
          value={itd.ties ? "Yes" : usd(itd.residual.usd)}
          tone={itd.ties ? "good" : "bad"}
          hint="Checked against the cash accounts' own balances, not this report's sums."
        />
      </KpiRow>

      <Grid
        headers={[
          "Section / account",
          { name: "MTD", right: true },
          { name: "YTD", right: true },
          { name: "ITD", right: true },
          { name: "Native currency", right: true },
        ]}
      >
        {rows.map((row, i) => (
          <Row key={i} kind={row.kind} cells={row.cells} />
        ))}
      </Grid>

      <Section title="Reconciliation to the cash accounts">
        <Grid
          headers={[
            "Period",
            { name: "Opening cash", right: true },
            { name: "Net change", right: true },
            { name: "Closing cash", right: true },
            { name: "Residual", right: true },
          ]}
        >
          {["mtd", "ytd", "itd"].map((period) => {
            const tie = report.reconciliation[period];
            return (
              <Row
                key={period}
                kind="line"
                cells={[
                  { value: period.toUpperCase() },
                  { value: usd(tie.opening_cash.usd), right: true },
                  { value: usd(tie.net_change.usd), right: true },
                  { value: usd(tie.closing_cash.usd), right: true },
                  {
                    value: tie.ties ? "reconciles" : usd(tie.residual.usd),
                    right: true,
                    muted: tie.ties,
                  },
                ]}
              />
            );
          })}
        </Grid>
      </Section>

      <Section title="How it is derived">
        <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: 12, lineHeight: 1.6, color: C.muted }}>
          <li>{report.how_it_is_derived}</li>
          <li>{report.tie_note}</li>
          <li>
            <strong>Between our own accounts:</strong> {num(internal.count)}{" "}
            transactions, {usd(internal.usd)}. Cash moved and nothing was
            funded, so none of it is in any section above — a wire from the bank
            to the broker is not an investing outflow.
          </li>
          <li>
            <strong>Split across sections:</strong> {num(mixed.count)}{" "}
            transactions carrying {usd(mixed.usd)} of cash touched more than one
            family and were allocated pro rata by their non-cash legs. That
            allocation is the only judgement in this statement.
          </li>
          <li>{report.rounding}</li>
        </ul>
      </Section>
    </>
  );
}

/* ─── Realized ───────────────────────────────────────────────── */

/* ─── Trial balance ──────────────────────────────────────────── */

/* One row per account AND currency. It used to add krona to dollars, which
   balanced while being wrong on both sides — and a trial balance that
   balances is the one report nobody re-reads. So the currency is a column,
   the balance check is per currency, and both are on the face of it. */
function TrialBalanceView({ report }) {
  const check = report.balance_check;

  const rows = [];
  let currentType = null;
  report.rows.forEach((row) => {
    if (row.type !== currentType) {
      currentType = row.type;
      rows.push({
        kind: "section",
        cells: [{ value: typeLabel(currentType) }, {}, {}, {}, {}, {}, {}],
      });
    }
    rows.push({
      kind: "line",
      cells: [
        { value: row.account, title: row.label },
        { value: row.currency },
        { value: num(row.entries), right: true, muted: true },
        { value: nf(2).format(Number(row.debit)), right: true },
        { value: nf(2).format(Number(row.credit)), right: true },
        { value: nf(2).format(Number(row.net)), right: true },
        { value: usd(row.usd), right: true, muted: true },
      ],
    });
  });

  return (
    <>
      <FxBanner report={report} />
      {!check.balanced && (
        <Banner tone="bad" title="This book does not balance.">
          Debits and credits differ in{" "}
          {check.per_currency.filter((c) => !c.balanced)
            .map((c) => `${c.currency} ${nf(2).format(Number(c.residual))}`)
            .join(", ")}
          . A residual means something posted one-sided, and the ledger is
          append-only, so the correction is a new balancing entry rather than
          an edit.
        </Banner>
      )}

      <KpiRow>
        <Kpi name="Accounts" value={num(report.totals.accounts)} />
        <Kpi
          name="Rows (account × currency)"
          value={num(report.totals.rows)}
          hint="One account in two currencies is two rows."
        />
        <Kpi name="Currencies" value={report.totals.currencies.join(", ") || "—"} />
        <Kpi
          name="Balance check"
          value={check.balanced ? "Balanced" : "Out"}
          tone={check.balanced ? "good" : "bad"}
          hint={check.balanced ? undefined : usd(check.usd_residual)}
        />
      </KpiRow>

      <Section title="Debits equal credits, in every currency">
        <Grid
          headers={[
            "Currency",
            { name: "Debits", right: true },
            { name: "Credits", right: true },
            { name: "Residual", right: true },
            "Balanced",
          ]}
        >
          {check.per_currency.map((c) => (
            <tr key={c.currency}>
              <td style={td}>{c.currency}</td>
              <td style={tdR}>{nf(2).format(Number(c.debits))}</td>
              <td style={tdR}>{nf(2).format(Number(c.credits))}</td>
              <td style={tdR}>{nf(2).format(Number(c.residual))}</td>
              <td style={{ ...td, color: c.balanced ? C.green : C.red, fontWeight: 600 }}>
                {c.balanced ? "yes" : "NO"}
              </td>
            </tr>
          ))}
        </Grid>
      </Section>

      <Section title="Account balances">
        <Grid
          headers={[
            "Account",
            "Ccy",
            { name: "Entries", right: true },
            { name: "Debits", right: true },
            { name: "Credits", right: true },
            { name: "Net", right: true },
            { name: "Net (USD)", right: true },
          ]}
        >
          {rows.map((row, i) => (
            <Row key={i} kind={row.kind} cells={row.cells} />
          ))}
        </Grid>
      </Section>

      <Notes report={report} />
    </>
  );
}

/* ─── Portfolio exposure ─────────────────────────────────────── */

/* One spelling of "we could not compute this", and it is not a dash: a
   withheld weight has a cause the reader can act on. */
function weight(value) {
  if (value === null || value === undefined) return "withheld";
  return `${nf(2).format(Number(value))}%`;
}

function WeightsBanner({ report }) {
  const concentration = report?.concentration;
  if (!concentration || concentration.measured) return null;
  return (
    <Banner tone="bad" title="Every weight on this report is withheld.">
      {concentration.withheld_because}
    </Banner>
  );
}

function ExposureView({ report }) {
  const totals = report.totals;
  const conc = report.concentration;
  const reliance = report.valuation_reliance;

  return (
    <>
      <FxBanner report={report} />
      <WeightsBanner report={report} />

      <KpiRow>
        <Kpi name="Positions" value={num(totals.positions)} />
        <Kpi name="Companies" value={num(totals.companies)} />
        <Kpi name="Cost" value={usd(totals.cost_usd, { cents: false })} />
        <Kpi
          name="Value"
          value={usd(totals.market_value_usd, { cents: false })}
          hint={native(totals.market_value_by_currency)}
          tone={reliance.is_a_floor ? "bad" : undefined}
        />
        <Kpi
          name="Largest company"
          value={conc.measured ? weight(conc.top_1_pct) : "withheld"}
          hint={conc.measured ? conc.largest.company : undefined}
        />
        <Kpi
          name="Top 5"
          value={conc.measured ? weight(conc.top_5_pct) : "withheld"}
        />
        {totals.short_positions > 0 && (
          <Kpi
            name="Gross exposure"
            value={usd(totals.gross_exposure_usd, { cents: false })}
            hint={`${totals.short_positions} short — long plus |short|. Weights divide by the net figure.`}
          />
        )}
      </KpiRow>

      {conc.measured &&
        Object.keys(conc.spelling_variants || {}).length > 0 && (
          <Banner tone="warn" title="One issuer, more than one spelling on the deal records.">
            Grouped here as one company so the concentration figure is right.
            The records should be fixed so every other report agrees:{" "}
            {Object.entries(conc.spelling_variants)
              .map(([name, spellings]) => `${name} = ${spellings.join(" / ")}`)
              .join("; ")}
            .
          </Banner>
        )}

      {reliance.is_a_floor && (
        <Banner tone="warn" title="This value is a floor, not a valuation.">
          {reliance.at_cost_price_expected} position
          {reliance.at_cost_price_expected === 1 ? " is" : "s are"} carried at
          cost because a price was expected and did not load —{" "}
          {usd(reliance.market_value_at_cost_where_a_price_was_expected_usd)}
          {" "}of it. Run <code>pipe.py fetch-prices --commit</code>. What is
          at cost by ruling (a note, a preferred, a PPLN, an LP interest) is
          not a gap and is counted separately.
        </Banner>
      )}

      <Section title="Largest positions">
        <Grid
          headers={[
            "Company / security",
            "Type",
            "Ccy",
            { name: "Cost", right: true },
            { name: "Value", right: true },
            { name: "Value (USD)", right: true },
            { name: "Weight", right: true },
            "How it was valued",
          ]}
        >
          {report.largest_positions.map((line, i) => (
            <tr key={i}>
              <td style={td}>
                {line.company || "(untagged)"}
                <div style={{ fontSize: 10.5, color: C.muted }}>
                  {line.ticker || line.security || "—"}
                  {line.deal_name ? ` · ${line.deal_name}` : ""}
                </div>
              </td>
              <td style={td}>{line.instrument_type || "—"}</td>
              <td style={td}>{line.currency}</td>
              <td style={tdR}>{nf(2).format(Number(line.cost))}</td>
              <td style={tdR}>{nf(2).format(Number(line.market_value))}</td>
              <td style={tdR}>{usd(line.market_value_usd)}</td>
              <td style={{ ...tdR, fontWeight: 600 }}>
                {weight(line.weight_pct)}
              </td>
              <td style={{ ...td, fontSize: 11, color: C.muted }}>
                {line.valuation_method}
              </td>
            </tr>
          ))}
          <Row
            kind="total"
            cells={[
              { value: `TOTAL — all ${report.positions.length} positions` },
              {}, {},
              { value: native(totals.cost_by_currency), right: true },
              { value: native(totals.market_value_by_currency), right: true },
              { value: usd(totals.market_value_usd), right: true },
              { value: conc.measured ? "100.00%" : "withheld", right: true },
              {},
            ]}
          />
        </Grid>
      </Section>

      {conc.measured && (
        <Section title="Concentration, by company">
          <Grid
            headers={["Measure", { name: "Value", right: true }, "What it is"]}
          >
            {[
              [
                "Largest company",
                `${conc.largest.company} · ${weight(conc.largest.weight_pct)}`,
                "The single biggest exposure, all its instruments added.",
              ],
              ["Top 5 companies", weight(conc.top_5_pct),
                "Share of the portfolio in its five largest names."],
              ["Top 10 companies", weight(conc.top_10_pct),
                "Share of the portfolio in its ten largest names."],
              ["Herfindahl index", num(conc.herfindahl_index),
                "Sum of squared weights × 10,000. 10,000 is one position. " +
                "The number is given and the adjective is not — the usual " +
                "1,500 threshold is a convention about liquid public " +
                "portfolios, which this is not."],
              ["Effective positions", num(conc.effective_positions, 1),
                "10,000 over the index — how many equally-sized names the " +
                "portfolio behaves like."],
              ["Companies held", num(conc.companies), ""],
            ].map(([name, value, why]) => (
              <tr key={name}>
                <td style={td}>{name}</td>
                <td style={{ ...tdR, fontWeight: 600 }}>{value}</td>
                <td style={{ ...td, fontSize: 11, color: C.muted }}>{why}</td>
              </tr>
            ))}
          </Grid>
          <p style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
            {conc.basis}
          </p>
        </Section>
      )}

      {report.breakdowns
        .filter((b) => b.rows.length > 1)
        .map((breakdown) => (
          <Section key={breakdown.dimension} title={`By ${breakdown.label.toLowerCase()}`}>
            <Grid
              headers={[
                breakdown.label,
                { name: "Positions", right: true },
                { name: "Cost (USD)", right: true },
                { name: "Value (USD)", right: true },
                { name: "In currency", right: true },
                { name: "Weight", right: true },
              ]}
            >
              {breakdown.rows.map((row) => (
                <tr key={row.name}>
                  <td style={td}>{row.name}</td>
                  <td style={tdR}>{num(row.positions)}</td>
                  <td style={tdR}>{usd(row.cost_usd)}</td>
                  <td style={tdR}>{usd(row.market_value_usd)}</td>
                  <td style={{ ...tdR, fontSize: 11, color: C.muted }}>
                    {native(row.market_value_by_currency)}
                  </td>
                  <td style={{ ...tdR, fontWeight: 600 }}>{weight(row.weight_pct)}</td>
                </tr>
              ))}
            </Grid>
          </Section>
        ))}

      <Section title="What the valuation is standing on">
        <Grid headers={["Basis", { name: "Positions", right: true }, { name: "Value (USD)", right: true }]}>
          <tr>
            <td style={td}>Marked to market</td>
            <td style={tdR}>{num(reliance.marked_to_market)}</td>
            <td style={tdR}>—</td>
          </tr>
          <tr>
            <td style={td}>At cost — a price was expected</td>
            <td style={tdR}>{num(reliance.at_cost_price_expected)}</td>
            <td style={tdR}>
              {usd(reliance.market_value_at_cost_where_a_price_was_expected_usd)}
            </td>
          </tr>
          <tr>
            <td style={td}>At cost by ruling</td>
            <td style={tdR}>{num(reliance.at_cost_by_policy)}</td>
            <td style={tdR}>—</td>
          </tr>
        </Grid>
        <p style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
          {reliance.why_it_matters}
        </p>
      </Section>

      <Section title="How to read this">
        <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, margin: 0 }}>
          {report.how_to_read_this}
        </p>
      </Section>
    </>
  );
}

/* ─── Risk ───────────────────────────────────────────────────── */

const SEVERITY = { high: C.red, medium: C.gold, low: C.muted };

function RiskView({ report }) {
  const counts = report.flag_counts;
  const conc = report.concentration;
  const currency = report.currency;

  return (
    <>
      <FxBanner report={report} />
      <WeightsBanner report={report} />

      <KpiRow>
        <Kpi name="Positions" value={num(report.totals.positions)} />
        <Kpi
          name="Value"
          value={usd(report.totals.market_value_usd, { cents: false })}
          hint={native(report.totals.market_value_by_currency)}
        />
        <Kpi
          name="High severity"
          value={num(counts.high)}
          tone={counts.high ? "bad" : "good"}
        />
        <Kpi name="Medium" value={num(counts.medium)} />
        <Kpi name="Low" value={num(counts.low)} />
        <Kpi
          name="Foreign currency"
          value={weight(currency.foreign_currency_weight_pct)}
          hint={`${num(currency.foreign_currency_positions)} positions, no hedges`}
        />
        <Kpi
          name="Gross exposure"
          value={usd(report.totals.gross_exposure_usd, { cents: false })}
          hint="Long plus the absolute value of short."
        />
      </KpiRow>

      <Section title="What needs attention">
        {report.flags.length === 0 ? (
          <Banner tone="good" title="No flag was raised.">
            Every check ran and found nothing. A flag with a count of nil is
            not printed, so an empty list here means the checks passed rather
            than that they were skipped.
          </Banner>
        ) : (
          <Grid
            headers={[
              "Flag", "Severity", { name: "Count", right: true },
              "What it means",
            ]}
          >
            {report.flags.map((flag) => (
              <tr key={flag.flag}>
                <td style={{ ...td, fontWeight: 600 }}>
                  {flag.flag.replace(/_/g, " ")}
                </td>
                <td style={{
                  ...td, color: SEVERITY[flag.severity], fontWeight: 700,
                  textTransform: "uppercase", fontSize: 10.5,
                }}>
                  {flag.severity}
                </td>
                <td style={tdR}>{num(flag.count)}</td>
                <td style={{ ...td, fontSize: 11.5, lineHeight: 1.5 }}>
                  {flag.what_it_means}
                </td>
              </tr>
            ))}
          </Grid>
        )}
      </Section>

      {conc.measured && (
        <Section title="Concentration">
          <Grid headers={["Measure", { name: "Value", right: true }]}>
            {[
              ["Largest company",
                `${conc.largest.company} · ${weight(conc.largest.weight_pct)}`],
              ["Top 5 companies", weight(conc.top_5_pct)],
              ["Top 10 companies", weight(conc.top_10_pct)],
              ["Herfindahl index", num(conc.herfindahl_index)],
              ["Effective positions", num(conc.effective_positions, 1)],
              ["Companies held", num(conc.companies)],
            ].map(([name, value]) => (
              <tr key={name}>
                <td style={td}>{name}</td>
                <td style={{ ...tdR, fontWeight: 600 }}>{value}</td>
              </tr>
            ))}
          </Grid>
        </Section>
      )}

      <Section title="Currency">
        <Grid
          headers={[
            "Currency", { name: "Positions", right: true },
            { name: "Value", right: true }, { name: "Value (USD)", right: true },
            { name: "Weight", right: true }, "Rate loaded",
          ]}
        >
          {currency.by_currency.map((row) => (
            <tr key={row.currency}>
              <td style={td}>
                {row.currency}
                {row.is_functional_currency && (
                  <span style={{ color: C.muted, fontSize: 10.5 }}> · functional</span>
                )}
              </td>
              <td style={tdR}>{num(row.positions)}</td>
              <td style={tdR}>{nf(2).format(Number(row.market_value))}</td>
              <td style={tdR}>{usd(row.market_value_usd)}</td>
              <td style={tdR}>{weight(row.weight_pct)}</td>
              <td style={{ ...td, color: row.rate_loaded ? C.green : C.red, fontWeight: 600 }}>
                {row.rate_loaded ? "yes" : "NO"}
              </td>
            </tr>
          ))}
        </Grid>
        <p style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
          {currency.hedging_note}
        </p>
      </Section>

      <Section title="Liquidity">
        <Grid
          headers={[
            "Tier", { name: "Positions", right: true },
            { name: "Value (USD)", right: true }, { name: "Weight", right: true },
            "What it is",
          ]}
        >
          {report.liquidity.tiers.map((row) => (
            <tr key={row.tier}>
              <td style={{ ...td, fontWeight: 600 }}>{row.label}</td>
              <td style={tdR}>{num(row.positions)}</td>
              <td style={tdR}>{usd(row.market_value_usd)}</td>
              <td style={tdR}>{weight(row.weight_pct)}</td>
              <td style={{ ...td, fontSize: 11, color: C.muted }}>{row.why}</td>
            </tr>
          ))}
        </Grid>
        <p style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
          {report.liquidity.basis}
        </p>
      </Section>

      <Section title="Maturity ladder">
        <Grid
          headers={[
            "Bucket", { name: "Positions", right: true },
            { name: "Carrying value (USD)", right: true },
            { name: "In currency", right: true },
            { name: "Weight", right: true },
          ]}
        >
          {report.maturity_ladder.rungs.map((row) => (
            <tr key={row.bucket}>
              <td style={{
                ...td,
                fontWeight: row.bucket === "past_due" ? 700 : 400,
                color: row.bucket === "past_due" && row.positions ? C.red : C.text,
              }}>
                {row.label}
              </td>
              <td style={tdR}>{num(row.positions)}</td>
              <td style={tdR}>{usd(row.carrying_value_usd)}</td>
              <td style={{ ...tdR, fontSize: 11, color: C.muted }}>
                {native(row.carrying_value_by_currency)}
              </td>
              <td style={tdR}>{weight(row.weight_pct)}</td>
            </tr>
          ))}
        </Grid>
        <p style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
          {report.maturity_ladder.basis}
        </p>
      </Section>

      {report.shorts.lines.length > 0 && (
        <Section title="Short positions">
          <Grid
            headers={[
              "Company", "Security",
              { name: "Shares short", right: true },
              { name: "Price", right: true },
              { name: "Cost to close", right: true },
              { name: "Proceeds received", right: true },
              { name: "Same issuer long", right: true },
            ]}
          >
            {report.shorts.lines.map((line, i) => (
              <tr key={i}>
                <td style={td}>{line.company || "—"}</td>
                <td style={td}>{line.security || "—"}</td>
                <td style={tdR}>{num(line.shares_short)}</td>
                <td style={tdR}>{num(line.price, 4)}</td>
                <td style={{ ...tdR, fontWeight: 600 }}>
                  {nf(2).format(Number(line.cost_to_close))} {line.currency}
                </td>
                <td style={tdR}>
                  {nf(2).format(Number(line.proceeds_received))} {line.currency}
                </td>
                <td style={tdR}>{usd(line.same_issuer_long_usd)}</td>
              </tr>
            ))}
          </Grid>
          <p style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
            {report.shorts.basis.replace(/\*\*/g, "")}
          </p>
        </Section>
      )}

      {report.share_obligations.obligations.length > 0 && (
        <Section title="Shares owed back">
          <Grid
            headers={[
              "Counterparty", "Company", "Security", "Arose",
              { name: "Shares owed", right: true },
              { name: "Shares held", right: true },
              { name: "Shortfall", right: true },
            ]}
          >
            {report.share_obligations.obligations.map((line) => (
              <tr key={line.obligation_id}>
                <td style={td}>{line.counterparty || "—"}</td>
                <td style={td}>{line.company || "—"}</td>
                <td style={td}>{line.security || "—"}</td>
                <td style={td}>{when(line.arose_on)}</td>
                <td style={tdR}>{num(line.shares_owed)}</td>
                <td style={tdR}>{num(line.shares_held_of_the_same_security)}</td>
                <td style={{
                  ...tdR, fontWeight: 600,
                  color: Number(line.shortfall) > 0 ? C.red : C.text,
                }}>
                  {Number(line.shortfall) > 0 ? num(line.shortfall) : "—"}
                </td>
              </tr>
            ))}
          </Grid>
          <p style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.6 }}>
            {report.share_obligations.basis}
          </p>
        </Section>
      )}

      <Section title="What this report cannot say">
        <ul style={{
          margin: "4px 0 0 18px", padding: 0, fontSize: 12, lineHeight: 1.7,
          color: C.muted,
        }}>
          {report.what_this_report_cannot_say.map((sentence, i) => (
            <li key={i}>{sentence.replace(/\*\*/g, "")}</li>
          ))}
        </ul>
        <p style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.6 }}>
          {report.how_to_read_this}
        </p>
      </Section>
    </>
  );
}

function RealizedView({ report }) {
  const totals = report.totals.itd;
  return (
    <>
      <FxBanner report={report} />
      <KpiRow>
        <Kpi
          name="Realized ITD, from disposals"
          value={usd(totals.from_lots.usd, { cents: false })}
          hint="The sum of the Sell and Cover rows."
        />
        <Kpi
          name="Realized ITD, from the ledger"
          value={usd(totals.from_ledger.usd, { cents: false })}
          hint="Income:Realized Gains less Expense:RealizedLoss."
        />
        <Kpi
          name="Difference"
          value={usd(totals.difference.usd, { cents: false })}
          tone={totals.difference.agrees ? "good" : undefined}
          hint="Often correct — a conversion earns its discount with no sale behind it."
        />
        <Kpi name="Deals" value={num(totals.deals)} />
        <Kpi
          name="Deals with uncovered sales"
          value={num(totals.deals_with_uncovered_sales)}
          tone={totals.deals_with_uncovered_sales ? "bad" : "good"}
          hint="A sale that found no lot took its whole proceeds into gain. Those deals are not fully loaded."
        />
      </KpiRow>

      <Grid
        headers={[
          "Deal / position",
          { name: "MTD (lots)", right: true },
          { name: "YTD (lots)", right: true },
          { name: "ITD (lots)", right: true },
          { name: "ITD (ledger)", right: true },
          { name: "Difference", right: true },
        ]}
      >
        {report.deals.map((deal) => [
          <Row
            key={`d${deal.deal_id}`}
            kind="group"
            cells={[
              {
                value: (
                  <>
                    {deal.company || "(untagged)"}
                    <div style={{ fontSize: 10, color: C.muted }}>
                      {deal.deal_name}
                      {deal.funded ? ` · funded ${deal.funded}` : ""}
                    </div>
                  </>
                ),
              },
              { value: usd(deal.mtd.from_lots.usd), right: true },
              { value: usd(deal.ytd.from_lots.usd), right: true },
              { value: usd(deal.itd.from_lots.usd), right: true },
              { value: usd(deal.itd.from_ledger.usd), right: true },
              {
                value: deal.itd.difference.agrees
                  ? "agrees"
                  : usd(deal.itd.difference.usd),
                right: true,
              },
            ]}
          />,
          ...deal.positions.map((position) => (
            <Row
              key={`p${deal.deal_id}-${position.security_id}`}
              kind="line"
              cells={[
                {
                  value: (
                    <>
                      {position.ticker || position.security || "—"}
                      {position.itd.uncovered_rows > 0 && (
                        <span style={{ color: C.red, fontSize: 10 }}>
                          {" "}
                          · {position.itd.uncovered_rows} uncovered
                        </span>
                      )}
                    </>
                  ),
                },
                { value: usd(position.mtd.realized_usd), right: true },
                { value: usd(position.ytd.realized_usd), right: true },
                { value: usd(position.itd.realized_usd), right: true },
                { value: "", right: true },
                {
                  value: native({ [position.itd.currency]: position.itd.realized }),
                  right: true,
                  muted: true,
                },
              ]}
            />
          )),
        ])}
        <Row
          kind="total"
          cells={[
            { value: "TOTAL" },
            { value: usd(report.totals.mtd.from_lots.usd), right: true },
            { value: usd(report.totals.ytd.from_lots.usd), right: true },
            { value: usd(totals.from_lots.usd), right: true },
            { value: usd(totals.from_ledger.usd), right: true },
            { value: usd(totals.difference.usd), right: true },
          ]}
        />
      </Grid>

      <Section title="Why the two columns can differ, legitimately">
        <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: 12, lineHeight: 1.6, color: C.muted }}>
          {report.why_the_two_can_differ.map((reason, i) => (
            <li key={i}>{reason}</li>
          ))}
        </ul>
      </Section>

      {report.deals_with_ledger_gain_and_no_disposal?.length > 0 && (
        <Banner title="Realized P&amp;L in the ledger with no disposal row at all:">
          {report.deals_with_ledger_gain_and_no_disposal
            .map((d) => d.company || d.deal_name || "(untagged)")
            .join(", ")}
          . A conversion or a paydown is exactly this shape and is correct; a
          deal here with neither is the finding.
        </Banner>
      )}
    </>
  );
}

/* ─── Unrealized ─────────────────────────────────────────────── */

function UnrealizedView({ report }) {
  const t = report.totals;
  return (
    <>
      <FxBanner report={report} />
      <KpiRow>
        <Kpi name="Cost" value={usd(t.cost.usd, { cents: false })} />
        <Kpi name="Carrying / market value" value={usd(t.market_value.usd, { cents: false })} />
        <Kpi
          name="Unrealized"
          value={usd(t.unrealized.usd, { cents: false })}
          tone={Number(t.unrealized.usd) >= 0 ? "good" : "bad"}
        />
        <Kpi name="Marked to market" value={num(t.marked_to_market)} />
        <Kpi
          name="Carried at cost"
          value={num(t.carried_at_cost)}
          hint="A note and a preferred at cost is the ruling. An unpriced equity at cost is a gap — see below."
        />
      </KpiRow>

      {report.unpriced?.length > 0 && (
        <Banner title={`${report.unpriced.length} positions are carried at cost for want of a price.`}>
          A line at cost is not a mark. Read <code>valuation_method</code> on the
          line, never a <code>not_priced_reason</code> on the security — the
          first is computed every run, the second is a claim somebody made once.
          Run <code>pipe.py fetch-prices --commit</code> after setting each
          security's Yahoo symbol.
        </Banner>
      )}

      <Grid
        headers={[
          "Deal / security",
          "Type",
          { name: "Quantity", right: true },
          { name: "Cost", right: true },
          { name: "Price", right: true },
          "Price date",
          { name: "Value", right: true },
          { name: "Unrealized (USD)", right: true },
          "How it was valued",
        ]}
      >
        {report.positions.map((line) => (
          <Row
            key={`${line.deal_id}-${line.security_id}`}
            kind="line"
            cells={[
              {
                value: (
                  <>
                    {line.company || "(untagged)"}
                    <div style={{ fontSize: 10, color: C.muted }}>
                      {line.ticker || line.security}
                      {line.currency ? ` · ${line.currency}` : ""}
                      {line.currency_assumed ? " · currency not recorded" : ""}
                    </div>
                  </>
                ),
              },
              { value: line.instrument_type || "—" },
              { value: num(line.quantity, 0), right: true },
              { value: native({ [line.currency]: line.cost }), right: true },
              { value: line.price ? nf(4).format(Number(line.price)) : "—", right: true },
              { value: when(line.price_date) },
              { value: native({ [line.currency]: line.market_value }), right: true },
              { value: usd(line.unrealized_usd), right: true },
              { value: line.valuation_method, muted: !line.is_marked },
            ]}
          />
        ))}
        <Row
          kind="total"
          cells={[
            { value: "TOTAL" },
            { value: "" },
            { value: "", right: true },
            { value: native(t.cost.by_currency), right: true },
            { value: "", right: true },
            { value: "" },
            { value: native(t.market_value.by_currency), right: true },
            { value: usd(t.unrealized.usd), right: true },
            { value: "" },
          ]}
        />
      </Grid>

      {report.unpriced?.length > 0 && (
        <Section title="Held with no price — carried at cost">
          <Grid
            headers={[
              "Security",
              { name: "Quantity", right: true },
              { name: "Cost", right: true },
              "Yahoo symbol",
              "Why",
              "Reason on file",
            ]}
          >
            {report.unpriced.map((row) => (
              <Row
                key={row.security_id}
                kind="line"
                cells={[
                  { value: `${row.ticker || "—"} — ${row.name || "—"}` },
                  { value: num(row.quantity), right: true },
                  { value: native({ [row.currency]: row.cost }), right: true },
                  { value: row.yahoo_symbol || "— none set", muted: !row.yahoo_symbol },
                  { value: row.why, muted: true },
                  { value: row.not_priced_reason_on_file || "—", muted: true },
                ]}
              />
            ))}
          </Grid>
          <p style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
            The reason on file is quoted, not believed: on 9 September 2026 both
            of the ones that mattered were false — “confirm current ticker” on a
            name whose new ticker had been recorded five days earlier, and
            “position closed” on 40,000 open shares.
          </p>
        </Section>
      )}

      {report.securities_with_no_currency?.length > 0 && (
        <Banner tone="bad" title="Securities with no currency recorded:">
          {report.securities_with_no_currency
            .map((s) => `${s.ticker} (${s.name})`)
            .join(", ")}
          . A blank currency is a bug, not a default — it is read as dollars
          here and that is an assumption, not a record.
        </Banner>
      )}

      <Section title="How each line was valued">
        <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, margin: 0 }}>
          {report.how_each_line_was_valued}
        </p>
        <p style={{ fontSize: 12, color: C.muted, lineHeight: 1.6, marginBottom: 0 }}>
          {report.accrued_interest_note}
        </p>
      </Section>
    </>
  );
}

/* ─── Shared furniture ───────────────────────────────────────── */

function Section({ title, children }) {
  return (
    <div style={{ marginTop: 18 }}>
      <h4 style={{
        fontSize: 13, fontWeight: 700, color: C.navy, margin: "0 0 6px",
        fontFamily: "Georgia, serif",
      }}>
        {title}
      </h4>
      {children}
    </div>
  );
}

function Notes({ report }) {
  const assumed = report.currency_assumed;
  const unassigned = report.unassigned_entity;
  return (
    <Section title="How to read this">
      <ul style={{ margin: "4px 0 0 18px", padding: 0, fontSize: 12, lineHeight: 1.6, color: C.muted }}>
        {report.period_note && <li>{report.period_note}</li>}
        {report.rounding && <li>{report.rounding}</li>}
        {assumed && (
          <li>
            <strong>Currency recorded:</strong> {num(assumed.entries)} entries
            carry no currency at all and are read as {assumed.currency}, which
            is what the ledger has always silently assumed —{" "}
            {usd(assumed.amount)} of absolute movement rides on that
            assumption. A blank currency says nobody wrote one down; it is not
            a claim of dollars.
          </li>
        )}
        {unassigned && (
          <li>
            <strong>Excluded by the entity filter:</strong>{" "}
            {num(unassigned.entries)} ledger entries carry no entity at all (
            {usd(unassigned.amount)} of absolute movement) and are in no
            per-entity statement. A journal written with a deal and no entity
            lands here.
          </li>
        )}
      </ul>
    </Section>
  );
}

/* ─── A report card ──────────────────────────────────────────── */

function ReportCard({ report, lpAccounts, entities, deals, onView, onDownload, busy }) {
  const wants = (name) => report.params.some((p) => p.name === name);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [entityId, setEntityId] = useState("");
  const [dealId, setDealId] = useState("");
  const [lpId, setLpId] = useState("");

  const needsLp = report.params.some((p) => p.name === "lp_id" && p.required);
  const needsDeal = report.params.some((p) => p.name === "deal" && p.required);
  const ready = (!needsLp || lpId) && (!needsDeal || dealId);

  /* Any other whole-number parameter the catalogue declares — `top` on the
     exposure report, `stale_after_days` on the risk report. Driven off the
     catalogue rather than named here, so a new one appears on the card
     without this file changing: the catalogue is already the list the server
     reads, and a second list of parameter names is the second reader §0.21
     is about. */
  const numberParams = report.params.filter(
    (p) => p.type === "integer" && !["entity_id", "deal", "lp_id"].includes(p.name)
  );
  const [numbers, setNumbers] = useState({});

  /* A yes/no the catalogue declares — `review` on the investor report — is a
     checkbox, sent as 1 when ticked and left out otherwise, so the server's
     default holds unless somebody asks. */
  const flagParams = report.params.filter((p) => p.type === "boolean");
  const [flags, setFlags] = useState({});

  /* Two accounts that are one lender go out as one report — Adam Rabie and
     Paradox SPC Fund (§0.46). The catalogue names the pairs, so a new one
     appears here without this file changing. */
  const lpParam = report.params.find((p) => p.name === "lp_id");
  const consolidated = (lpParam && lpParam.consolidations) || [];

  const params = useMemo(() => {
    const out = {};
    if (wants("date")) out.date = date;
    if (wants("as_of")) out.as_of = date;
    if (wants("entity_id") && entityId) out.entity_id = entityId;
    if (wants("deal") && dealId) out.deal = dealId;
    if (wants("lp_id") && lpId) out.lp_id = lpId;
    numberParams.forEach((p) => {
      if (numbers[p.name] !== undefined && numbers[p.name] !== "") {
        out[p.name] = numbers[p.name];
      }
    });
    flagParams.forEach((p) => {
      if (flags[p.name]) out[p.name] = 1;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, entityId, dealId, lpId, numbers, flags, report]);

  const canView = !!VIEWABLE[report.id];

  return (
    <div style={{
      background: C.card, borderRadius: 10, border: `1px solid ${C.border}`,
      padding: 18, display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div>
        <div style={{
          fontSize: 15, fontWeight: 700, color: C.navy, marginBottom: 4,
          fontFamily: "Georgia, serif",
        }}>
          {report.name}
        </div>
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
          {report.description}
        </div>
        {report.caveat && (
          <div style={{
            marginTop: 8, fontSize: 11, color: C.text, background: C.amber,
            border: `1px solid ${C.amberLine}`, borderRadius: 5,
            padding: "6px 9px", lineHeight: 1.5,
          }}>
            <strong>Before you quote it: </strong>
            {report.caveat}
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
        {(wants("date") || wants("as_of")) && (
          <div>
            <label style={label}>As of</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={input} />
          </div>
        )}
        {wants("entity_id") && (
          <div style={{ flex: 1, minWidth: 150 }}>
            <label style={label}>Entity</label>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)} style={input}>
              <option value="">All entities</option>
              {entities.map((e) => (
                <option key={e.id} value={e.id}>{e.name}</option>
              ))}
            </select>
          </div>
        )}
        {wants("lp_id") && (
          <div style={{ flex: 1, minWidth: 170 }}>
            <label style={label}>Investor</label>
            <select value={lpId} onChange={(e) => setLpId(e.target.value)} style={input}>
              <option value="">Select investor…</option>
              {consolidated.length > 0 && (
                <optgroup label="Consolidated">
                  {consolidated.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </optgroup>
              )}
              <optgroup label="One account">
                {lpAccounts.map((lp) => (
                  <option key={lp.id} value={lp.id}>{lp.investor_name || lp.name}</option>
                ))}
              </optgroup>
            </select>
          </div>
        )}
        {numberParams.map((p) => (
          <div key={p.name} style={{ minWidth: 110 }}>
            <label style={label} title={p.description}>
              {p.name.replace(/_/g, " ")}
            </label>
            <input
              type="number"
              min="0"
              placeholder="default"
              value={numbers[p.name] ?? ""}
              onChange={(e) =>
                setNumbers((prev) => ({ ...prev, [p.name]: e.target.value }))
              }
              style={{ ...input, minWidth: 100 }}
              title={p.description}
            />
          </div>
        ))}
        {flagParams.map((p) => (
          <label
            key={p.name}
            title={p.description}
            style={{
              display: "flex", alignItems: "center", gap: 6, fontSize: 12,
              color: C.text, padding: "8px 0", cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={!!flags[p.name]}
              onChange={(e) =>
                setFlags((prev) => ({ ...prev, [p.name]: e.target.checked }))
              }
            />
            {p.label || p.name.replace(/_/g, " ")}
          </label>
        ))}
        {wants("deal") && (
          <div style={{ flex: 1, minWidth: 170 }}>
            <label style={label}>Deal</label>
            <select value={dealId} onChange={(e) => setDealId(e.target.value)} style={input}>
              <option value="">{needsDeal ? "Select deal…" : "All deals"}</option>
              {deals.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.deal_name}
                  {d.counterparty ? ` (${d.counterparty})` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {canView && (
          <button
            onClick={() => onView(report, params)}
            disabled={!ready || busy}
            style={{
              flex: "1 1 120px", padding: "9px 14px", borderRadius: 6,
              border: "none", background: ready ? C.navy : C.sand,
              color: ready ? C.cream : C.muted, fontSize: 12.5, fontWeight: 600,
              cursor: ready && !busy ? "pointer" : "default",
            }}
          >
            {busy ? "Running…" : "View on screen"}
          </button>
        )}
        {report.formats
          .filter((f) => f !== "json")
          .map((format) => (
            <button
              key={format}
              onClick={() => onDownload(report, params, format)}
              disabled={!ready}
              style={{
                flex: "0 1 auto", padding: "9px 14px", borderRadius: 6,
                border: `1px solid ${C.border}`, background: C.inputBg,
                color: ready ? C.text : C.muted, fontSize: 12.5,
                fontWeight: 600, cursor: ready ? "pointer" : "default",
              }}
            >
              {format === "pdf" ? "PDF" : "Excel"}
            </button>
          ))}
        {!canView && !report.formats.some((f) => f !== "json") && (
          <button
            onClick={() => onView(report, params)}
            disabled={!ready}
            style={{
              flex: "1 1 120px", padding: "9px 14px", borderRadius: 6,
              border: `1px solid ${C.border}`, background: C.inputBg,
              fontSize: 12.5, fontWeight: 600, cursor: ready ? "pointer" : "default",
            }}
          >
            Open JSON
          </button>
        )}
      </div>
    </div>
  );
}


/* ─── Page ───────────────────────────────────────────────────── */

export default function Reports() {
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [lpAccounts, setLpAccounts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [deals, setDeals] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/reports/catalog/")
      .then((r) => setCatalog(r.data))
      .catch((e) => setCatalogError(e?.message || "could not load the catalogue"));
    api.get("/investors/lp-accounts/?limit=200")
      .then((r) => setLpAccounts(r.data.results || r.data || []))
      .catch(() => {});
    api.get("/core/entities/?limit=200")
      .then((r) => setEntities(r.data.results || r.data || []))
      .catch(() => {});
    api.get("/securities/deals/?limit=500")
      .then((r) => setDeals(r.data.results || r.data || []))
      .catch(() => {});
  }, []);

  const view = useCallback(async (report, params) => {
    setBusy(true);
    setError(null);
    setViewing(report);
    setViewData(null);
    try {
      const response = await api.get(report.endpoint, {
        params: { ...params, format: "json" },
      });
      setViewData(response.data);
    } catch (e) {
      // Said plainly, with what the server said. A page that swallows the
      // reason is how "the report is broken" becomes a week of guessing.
      setError(
        e?.response?.data?.error ||
          e?.response?.data?.detail ||
          `${e?.response?.status || ""} ${e?.message || "request failed"}`.trim()
      );
    } finally {
      setBusy(false);
    }
  }, []);

  const download = useCallback(async (report, params, format) => {
    setError(null);
    try {
      const response = await api.get(report.endpoint, {
        params: { ...params, format },
        responseType: "blob",
      });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const name =
        (match && match[1]) ||
        `Helena_${report.id}_${params.date || "latest"}.${format === "excel" ? "xlsx" : format}`;
      const url = URL.createObjectURL(
        new Blob([response.data], { type: response.headers["content-type"] })
      );
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = name;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(
        `${report.name} (${format}) failed: ${
          e?.response?.status || ""
        } ${e?.message || "request failed"}`.trim()
      );
    }
  }, []);

  const families = catalog?.families || [];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{
          fontSize: 26, fontWeight: 700, color: C.navy, margin: "0 0 4px",
          fontFamily: "Georgia, serif",
        }}>
          Reports
        </h1>
        <p style={{ fontSize: 13, color: C.muted, margin: 0, maxWidth: 820, lineHeight: 1.55 }}>
          Every report the book can produce. The financial statements and the
          two P&amp;L reports read on screen as well as downloading, because a
          report nobody opens is a figure nobody checks. A dollar figure
          reading <strong>n/a</strong> is one the book could not convert — not
          one worth nothing.
        </p>
      </div>

      {catalogError && (
        <Banner tone="bad" title="The report catalogue would not load.">
          {catalogError}. Every card on this page is built from{" "}
          <code>GET /reports/catalog/</code>.
        </Banner>
      )}

      {error && (
        <Banner tone="bad" title="That report did not run.">
          {error}
        </Banner>
      )}

      {viewing && (
        <div style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
          padding: 20, marginBottom: 24,
        }}>
          <div style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "flex-start", gap: 12, marginBottom: 14,
          }}>
            <div>
              <h2 style={{
                fontSize: 19, fontWeight: 700, color: C.navy, margin: 0,
                fontFamily: "Georgia, serif",
              }}>
                {viewing.name}
              </h2>
              {viewData && (
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3 }}>
                  As of {when(viewData.as_of)}
                  {viewData.entity_id ? ` · entity ${viewData.entity_id}` : " · all entities"}
                  {viewData.deal_id ? ` · deal ${viewData.deal_id}` : ""}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {viewing.formats.filter((f) => f !== "json").map((format) => (
                <button
                  key={format}
                  onClick={() => download(viewing, { date: viewData?.as_of }, format)}
                  style={{
                    padding: "7px 12px", borderRadius: 6,
                    border: `1px solid ${C.border}`, background: C.inputBg,
                    fontSize: 12, fontWeight: 600, cursor: "pointer",
                  }}
                >
                  {format === "pdf" ? "PDF" : "Excel"}
                </button>
              ))}
              <button
                onClick={() => { setViewing(null); setViewData(null); }}
                style={{
                  padding: "7px 12px", borderRadius: 6, border: "none",
                  background: C.cream, color: C.muted, fontSize: 12,
                  fontWeight: 600, cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>

          {busy && <div style={{ fontSize: 13, color: C.muted }}>Running…</div>}
          {viewData && VIEWABLE[viewing.id] === "statement" && (
            <StatementView report={viewData} />
          )}
          {viewData && VIEWABLE[viewing.id] === "cashflow" && (
            <CashFlowView report={viewData} />
          )}
          {viewData && VIEWABLE[viewing.id] === "trialbalance" && (
            <TrialBalanceView report={viewData} />
          )}
          {viewData && VIEWABLE[viewing.id] === "exposure" && (
            <ExposureView report={viewData} />
          )}
          {viewData && VIEWABLE[viewing.id] === "risk" && (
            <RiskView report={viewData} />
          )}
          {viewData && VIEWABLE[viewing.id] === "realized" && (
            <RealizedView report={viewData} />
          )}
          {viewData && VIEWABLE[viewing.id] === "unrealized" && (
            <UnrealizedView report={viewData} />
          )}
          {viewData && VIEWABLE[viewing.id] === "repatriation" && (
            <RepatriationView report={viewData} />
          )}
          {viewData && !VIEWABLE[viewing.id] && (
            <pre style={{
              fontSize: 11, background: C.cream, padding: 12, borderRadius: 6,
              overflowX: "auto", maxHeight: 520, margin: 0,
            }}>
              {JSON.stringify(viewData, null, 2)}
            </pre>
          )}
        </div>
      )}

      {!catalog && !catalogError && (
        <div style={{ fontSize: 13, color: C.muted }}>Loading the catalogue…</div>
      )}

      {families.map((family) => (
        <div key={family} style={{ marginBottom: 28 }}>
          <h3 style={{
            fontSize: 15, fontWeight: 700, color: C.navy, margin: "0 0 12px",
            fontFamily: "Georgia, serif", display: "flex", alignItems: "center",
            gap: 8,
          }}>
            <span style={{ fontSize: 17 }}>{FAMILY_ICON[family] || "\u{1F4C4}"}</span>
            {family}
          </h3>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: 16,
          }}>
            {catalog.reports
              .filter((r) => r.family === family)
              .map((report) => (
                <ReportCard
                  key={report.id}
                  report={report}
                  lpAccounts={lpAccounts}
                  entities={entities}
                  deals={deals}
                  onView={view}
                  onDownload={download}
                  busy={busy && viewing?.id === report.id}
                />
              ))}
          </div>
        </div>
      ))}
    </div>
  );
}
